import { get } from '@rover/react-lib/src/apis/countryFooterLinks.api';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import type { CountryFooterLink, FetchCountryFooterLinksResponse } from '@rover/types';

import generateActions from './generateActions';

export type State = {
  errorMessage: string;
  loading: boolean;
  fetchCountryFooterLinksResponse: FetchCountryFooterLinksResponse;
};

type Actions = {
  fetchCountryFooterLinksPending: ActionCreator<void>;
  fetchCountryFooterLinksSuccess: ActionCreator<FetchCountryFooterLinksResponse>;
  fetchCountryFooterLinksFailure: ActionCreator<string>;
};

type Effects = {
  fetchCountryFooterLinks: EffectCreator<void>;
};

type Selectors = {
  getCountryFooterLinks: SelectorCreator<CountryFooterLink[]>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'countryFooterLinks',
  initialState: {
    errorMessage: '',
    loading: false,
    fetchCountryFooterLinksResponse: {
      results: [],
    },
  },
  actions: generateActions('fetchCountryFooterLinks'),
  effects: {
    fetchCountryFooterLinks:
      () =>
      async (
        dispatch,
        getState,
        {
          fetchCountryFooterLinksPending,
          fetchCountryFooterLinksSuccess,
          fetchCountryFooterLinksFailure,
        }
      ) => {
        dispatch(fetchCountryFooterLinksPending());

        try {
          const fetchCountryFooterLinksResponse: FetchCountryFooterLinksResponse = await get();
          dispatch(fetchCountryFooterLinksSuccess(fetchCountryFooterLinksResponse));
        } catch (e) {
          dispatch(fetchCountryFooterLinksFailure((e as Error).message));
          captureError(e as Error);
        }
      },
  },
  selectors: {
    getCountryFooterLinks: (getState, createSelector) =>
      createSelector([getState], (x) => x.fetchCountryFooterLinksResponse.results),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
